import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GitCompareArrows, ChevronDown, Check, Loader2, Info } from 'lucide-react';
import { reportService } from '../../services/reportService';

// =============================================================================
// What a report is being read against.
//
// Every report is about ONE budget version — the one in the app-wide selector —
// and is nearly always read beside a second set of numbers. This is the single
// control that picks the second one, so no report has to invent its own idea of
// "vs last year".
//
// UNAVAILABLE OPTIONS ARE SHOWN, DISABLED, WITH THE REASON. This is the same
// principle as the `runnable` flag on a report tile. Mantle imports actuals as
// separate feeds that arrive at different times: on a live tenant the general
// ledger can hold FY2025 while actual stock cost holds FY2026, so "Actuals
// FY2026" is a real thing to want and not yet a real thing to have. Hiding it
// makes the reader assume the feature does not exist and stop asking; showing
// it greyed out with "No general ledger imported for FY2026" tells them exactly
// which import to chase.
//
// The server decides all of this — which versions, which years, and whether the
// report can use them at all. This component only draws the answer.
// =============================================================================

export default function ComparePicker({ reportCode, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    if (sources) return;
    setLoading(true);
    try {
      const res = await reportService.getCompareSources(reportCode);
      setSources(res.data);
    } catch {
      // A picker that cannot load its options is not worth an error banner over
      // the report itself — it degrades to "no comparison", which is the state
      // the page is already in.
      setSources({ versions: [], actuals: [], actuals_note: null });
    } finally {
      setLoading(false);
    }
  }, [reportCode, sources]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Close on an outside click, so the panel behaves like every other dropdown
  // in the app rather than needing a deliberate dismissal.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const all = [...(sources?.versions || []), ...(sources?.actuals || [])];
  const selected = all.find((s) => s.key === value);
  const label = value ? (selected?.label || value) : 'No comparison';

  const pick = (key) => { onChange(key); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors
          ${value
            ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300'
            : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
      >
        <GitCompareArrows className="w-3.5 h-3.5" />
        <span>Compare: {label}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-80 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {loading ? (
            <div className="h-20 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="py-1.5 max-h-96 overflow-y-auto">
              <Option
                label="No comparison"
                sub="Show this version on its own"
                selected={!value}
                onClick={() => pick('')}
              />

              {(sources?.versions || []).length > 0 && <GroupLabel>Budget versions</GroupLabel>}
              {(sources?.versions || []).map((s) => (
                <Option key={s.key} label={s.label} sub={s.sub}
                        selected={value === s.key} onClick={() => pick(s.key)} />
              ))}

              {(sources?.actuals || []).length > 0 && <GroupLabel>Actuals</GroupLabel>}
              {(sources?.actuals || []).map((s) => (
                <Option key={s.key} label={s.label} sub={s.sub}
                        selected={value === s.key}
                        disabled={!s.available}
                        onClick={() => s.available && pick(s.key)} />
              ))}

              {/* Why there is no actuals group at all, when there is none. A
                  reader who is not told assumes it is coming; a reader who is
                  told knows it is an import away. */}
              {sources?.actuals_note && (
                <div className="mx-2 mt-1.5 mb-1 flex gap-2 px-2.5 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/40">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                  <p className="m-0 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {sources.actuals_note}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const GroupLabel = ({ children }) => (
  <div className="px-3 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{children}</div>
);

const Option = ({ label, sub, selected, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors
      ${disabled
        ? 'opacity-50 cursor-not-allowed'
        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
  >
    <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${selected ? 'text-blue-600' : 'opacity-0'}`} />
    <span className="min-w-0">
      <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">{label}</span>
      {sub && <span className="block text-[11px] leading-snug text-slate-500 dark:text-slate-400">{sub}</span>}
    </span>
  </button>
);
